import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SystemDashboardPage from '../components/system/SystemDashboardPage';
import FooterSystem from '../components/system/FooterSystem';

interface DashboardSystemProps {
  children?: React.ReactNode;
}

export default function DashboardSystem({ children }: DashboardSystemProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Force a page reload after sign out to ensure clean state
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Announcement Banner */}
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 flex items-center justify-center font-semibold text-center">
        Please Note: This is the manual system. Access the fully automated bot 👉 {' '}
        <a
          href="https://app.horseracingoracle.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-700 underline font-bold hover:text-primary-900 transition-colors"
        >
          HERE
        </a>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-gray-900 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="/HRO-Logo-transparent-white-logo-white-text.png"
                  alt="HRO Logo"
                  className="h-10"
                />
              </div>
            </div>

            {/* Right side menu items */}
            <div className="hidden lg:ml-4 lg:flex lg:items-center lg:space-x-4">
              <Link
                to="/dashboard/system"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/system/user-guide"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                User Guide
              </Link>
              <a
                href="/system/faqs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Support
              </a>
              <button
                onClick={handleSignOut}
                className="btn btn-secondary whitespace-nowrap"
              >
                Sign out
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#b29e84]"
              >
                <span className="sr-only">Open main menu</span>
                {/* Icon when menu is closed */}
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {/* Icon when menu is open */}
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:hidden bg-gray-900`}>
          <div className="border-t border-gray-800 pt-4 pb-3">
            <div className="space-y-1">
              <Link
                to="/dashboard/system"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/system/user-guide"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                User Guide
              </Link>
              <a
                href="/system/faqs"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Support
              </a>
              <button
                onClick={() => {
                  handleSignOut();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <SystemDashboardPage />
        </div>
      </main>

      {/* Footer */}
      <FooterSystem />
    </div>
  );
} 